import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { bmxurl, withSelectableIds } from 'src/globals';
import { AvailabilityReq } from 'src/PackageClasses';

//Moving a booking to another date or time - the one copy of how it is done, shared by Edit
//Booking's Advanced Edit and the AI staff assistant so the two can never move a booking
//differently. The building blocks are lifted from Advanced Edit unchanged:
//
//  1. Ask /Availability for the day, for the booking's package and admissions, ignoring the
//     booking's own place - it only supplies the times to offer.
//  2. Set the new start time, and move every one of the booking's own resources by the same
//     amount, each keeping its offset and length, venuedate re-pointed to the new day.
//  3. Refuse to save a booking left with no admissions.
//  4. Save the whole booking with PUT savebooking/true.
//
//Advanced Edit keeps its own screen and its resource swapping; it only uses these pieces. The
//assistant uses moveBooking(), which keeps the booking's resources and changes the time only.
@Injectable({
  providedIn: 'root'
})
export class MoveBookingService {

  constructor(private http: HttpClient) { }

  //#region The shared pieces

  //The /Availability request for one day. admissions are the package's admissions carrying the
  //booking's numbers; packageResources the package's own resource definition.
  //packageId is typed loosely: AvailabilityReq says number, but package ids are strings - Edit
  //Booking always passed its package's id straight through, and this does the same.
  availabilityRequest(venueId: string, packageId: any, admissions: any[], packageResources: any[], bookingId: string, day: Date): AvailabilityReq {
    const req = {} as AvailabilityReq;
    req.Admissions = [];
    let totalNoOfPeople = 0;

    (admissions ?? []).forEach((adm: any) => {
      req.Admissions.push(adm);
      totalNoOfPeople += adm.noofpeople;
    });

    req.packageId = packageId;
    req.NoOfPeople = totalNoOfPeople;
    req.venueid = venueId;
    req.StartTime = day.getFullYear() + '-' + (day.getMonth() + 1) + '-' + day.getDate() + 'T00:00:00.000Z';
    req.DiagnosticMode = false;
    req.IgnoreExistingBooking = true;
    req.ExistingBookingID = bookingId;
    req.Resources = packageResources;

    return req;
  }

  //The times worth offering from an /Availability reply: one per time, and only those with a
  //primary resource free.
  usableTimes(slots: any[]): any[] {
    const seen = new Set<string>();

    return (slots ?? []).filter(slot => {
      if (!slot.primaryResource) return false;
      if (seen.has(slot.time)) return false;
      seen.add(slot.time);
      return true;
    });
  }

  //The booking's own resources moved by however far the start time has moved, so each keeps its
  //own offset and length. venuedate is re-pointed only where the resource carries one.
  shiftResources(bookingResources: any[], fromStartTime: any, toStartTime: any, venueId: string, day: Date): any[] {
    const shiftMs = new Date(toStartTime).getTime() - new Date(fromStartTime).getTime();
    const newVenueDate = venueId + '_' + day.getFullYear() +
      ('0' + (day.getMonth() + 1)).slice(-2) + ('0' + day.getDate()).slice(-2);
    const shift = (t: any) => t ? new Date(new Date(t).getTime() + shiftMs).toISOString() : t;

    return (bookingResources ?? []).map((r: any) => ({
      ...r,
      startTime: shift(r.startTime),
      endTime: shift(r.endTime),
      venuedate: r.venuedate ? newVenueDate : r.venuedate
    }));
  }

  //A booking with no admissions is a broken booking - saving it would persist the loss.
  hasAdmissions(booking: any): boolean {
    if (booking == null || booking.resources == null || booking.resources.length == 0) {
      return false;
    }

    return booking.resources.some((res: any) => res.admissions != null && res.admissions.length > 0);
  }

  //#endregion

  //#region Whole moves, for callers without Edit Booking's screen

  //A booking, read fresh, with what a move needs alongside it: its package, and the package's
  //admissions carrying the booking's numbers (matched on admissionid, as Edit Booking does).
  async loadForMove(venueId: string, bookingId: string): Promise<{ booking: any, packageObj: any, admissions: any[] }> {
    const bookingResponse: any = await this.http.get(bmxurl + 'booking/' + venueId + '/' + bookingId).toPromise();

    if (!bookingResponse?.succeeded || bookingResponse.data == null) {
      throw new Error('That booking could not be opened.');
    }

    const booking = bookingResponse.data;
    const packageResponse: any = await this.http.get(bmxurl + 'package/' + venueId + '/' + booking.packageId).toPromise();

    if (!packageResponse?.succeeded || packageResponse.data == null) {
      throw new Error("The booking's package could not be read.");
    }

    const packageObj = packageResponse.data;
    const booked: any[] = booking.resources?.[0]?.admissions ?? [];

    const admissions = (packageObj.admissions ?? []).map((adm: any) => {
      const match = booked.find((b: any) => b.admissionid == adm.admissionid);
      return { ...adm, noofpeople: match != null ? match.noofpeople : 0 };
    });

    return { booking, packageObj, admissions };
  }

  //The times the booking could move to on a day.
  async timesOn(venueId: string, loaded: { booking: any, packageObj: any, admissions: any[] }, day: Date): Promise<any[]> {
    const req = this.availabilityRequest(venueId, loaded.packageObj.packageId, loaded.admissions, loaded.packageObj.resources, loaded.booking.bookingId, day);
    const response: any = await this.http.post(bmxurl + '/Availability', withSelectableIds(req)).toPromise();

    if (!response?.succeeded) {
      throw new Error('Availability could not be checked.');
    }

    return this.usableTimes(response.data ?? []);
  }

  //Moves a booking to one of the times timesOn() gave, keeping its resources. Returns the saved
  //booking. Nothing is saved if the move would leave the booking without admissions.
  async moveBooking(venueId: string, loaded: { booking: any }, slot: any, day: Date): Promise<any> {
    const booking = { ...loaded.booking };

    booking.resources = this.shiftResources(loaded.booking.resources, loaded.booking.startTime, slot.time, venueId, day);
    booking.startTime = slot.time;

    if (!this.hasAdmissions(booking)) {
      throw new Error('The move was stopped because the booking would have lost its admissions. Nothing was changed.');
    }

    const response: any = await this.http.put(bmxurl + '/savebooking/true', withSelectableIds(booking)).toPromise();

    if (!response?.succeeded) {
      throw new Error(response?.messages?.[0] ?? 'The booking could not be saved.');
    }

    return response.data;
  }

  //#endregion
}
